/* The shared conversation, as a hook: read it, ask into it, open a stored answer in it, clear it.
 *
 * **`mutateAsync`, not `mutate` with callbacks.** A `mutate` callback does not run once the component
 * that called it has unmounted — so a question asked on the `/copilot` page, followed by the reader
 * clicking away, would leave its turn pending for ever in a store that outlives the page. The promise
 * settles whoever is still mounted, and the store is what the reader comes back to.
 */

import { useCallback, useSyncExternalStore } from 'react';

import {
  clearThread,
  isAsking,
  subscribeThread,
  threadSnapshot,
  updateThread,
} from '../model/thread';
import type { Turn } from '../model/thread';

import { useAsk } from './copilot';
import type { StoredAnswer } from './copilot';

export interface CopilotThread {
  readonly turns: readonly Turn[];
  readonly asking: boolean;
  /** Ask a question into the conversation. Ignored while another is in flight, or when empty. */
  readonly send: (question: string) => void;
  /** Put a stored transcript answer into the conversation. Spends nothing: it was answered once. */
  readonly show: (stored: StoredAnswer) => void;
  readonly clear: () => void;
}

export function useCopilotThread(): CopilotThread {
  const turns = useSyncExternalStore(subscribeThread, threadSnapshot);
  const { mutateAsync } = useAsk();
  const asking = isAsking(turns);

  const send = useCallback(
    (question: string): void => {
      const trimmed = question.trim();
      if (trimmed === '' || isAsking(threadSnapshot())) return;
      const id = `ask-${crypto.randomUUID()}`;
      updateThread((current) => [
        ...current,
        { id, question: trimmed, state: 'pending' },
      ]);
      mutateAsync(trimmed)
        .then((answer) => {
          updateThread((current) =>
            current.map((turn) =>
              turn.id === id
                ? { id, question: trimmed, state: 'answered', answer }
                : turn,
            ),
          );
        })
        .catch((failure: unknown) => {
          const message = failure instanceof Error ? failure.message : String(failure);
          updateThread((current) =>
            current.map((turn) =>
              turn.id === id ? { id, question: trimmed, state: 'failed', message } : turn,
            ),
          );
        });
    },
    [mutateAsync],
  );

  const show = useCallback((stored: StoredAnswer): void => {
    const id = `stored-${stored.id}`;
    updateThread((current) =>
      /* Opening the same answer twice adds it once — it is one answer, not two turns. */
      current.some((turn) => turn.id === id)
        ? current
        : [
            ...current,
            { id, question: stored.question, state: 'answered', answer: stored },
          ],
    );
  }, []);

  return { turns, asking, send, show, clear: clearThread };
}
